import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, forkJoin, interval, map, NEVER, Observable, of, Subject, switchMap, take, takeUntil, tap, throwError } from 'rxjs';
import { Application } from 'src/portaljs/constants/application';
import { AppSettings } from '../models/app-settings.model';
import { ThemeService } from './theme.service';
import * as _ from 'lodash-es';
import { ConsoleLogger } from '../shared/console-logger';
import { environment } from 'src/environments/environment';
import { AppInfo } from '../models/app-info.model';
import { Util } from '../shared/util';
import { LOCATION_TOKEN } from '../constants/location.token';
// declare var browserPopupBlocked: any;
import {UN_CURRENT_VERSION} from '../constants/unified.navigiation';
import { DebugMessage, PortalDebugMessage } from '../models/portal-debug-message';
import { UserLoginModel } from '../models/user-login.model';

@Injectable({ providedIn: 'root' })
export class EnvironmentService implements OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private debugTimerUnsubscribe: Subject<void> | null = null;
  private appConfig: AppSettings;
  private appConfigOrig: AppSettings;
  public bxid: string;
  public appInfos: AppInfo[] = [];
  public shouldBeProxied: string = 'false';
  public portal_frontend_proxy_url: string;
  public browserPopupBlocked = false;
  public popupEnabled: string = 'true';
  public destinyBuildNumber: string;
  public destinyChatBotEnabledAtDistrict: string = 'false';
  private _appInitComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public appInitComplete$: Observable<boolean> = this._appInitComplete.asObservable();
  private _debugMessageQueue: PortalDebugMessage[] = [];
  private IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER = false;


  // private static readonly collectionsContext = '/collections/rest/authentication/v1/portal/login';
  private static readonly ddContext = '/v2/users/sso/portal/authenticate';
  private static readonly collectionsContext = '/portalLanding';
  private static readonly dboContext = '/common/welcome.jsp';
  private static readonly clmContext = '';
  private static readonly destinyAiContext = 'assistant';
  private static readonly juniperSidecarContext = 'assistant/sidecar';
  private static readonly singletonApps = [Application.DESTINY_AI, Application.COLLECTIONS, Application.DD, Application.DASHBOARD, Application.CLM, Application.FM, Application.PSARI, Application.UNIFIED_SETTINGS, Application.DESTINY_UI, Application.JUNIPER_SIDECAR];

  constructor(private http: HttpClient, private themeService: ThemeService, @Inject(LOCATION_TOKEN) private location: Location) {
    this.browserPopupBlocked = true;
  }

  public getAppInitComplete$(): Observable<boolean> {
    return this.appInitComplete$;
  }

  public ngOnDestroy(): void {
    this._stopDebugMessageTimer();
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this._appInitComplete.complete();
  }

  public loadAppConfig() {
    ConsoleLogger.debug('EnvSvc.loadAppConfig(): begin');
    return this.http.get('config/config.json')
      .pipe(
        map((data: any) => {
          this.appConfig = data;
          this.appConfigOrig = _.cloneDeep(this.appConfig);
          this.outputConfig();
          ConsoleLogger.debug('EnvSvc.loadAppConfig(): config loaded');
          this.themeService.loadThemeIntoDocument();
        }),
        switchMap(() => {
          return this.loadAppInfos();
        }),
        tap(() => ConsoleLogger.debug('EnvSvc.loadAppConfig(): appInfos loaded')),
        tap(() => ConsoleLogger.debug('EnvSvc.loadAppConfig(): complete')),
        take(1),
        takeUntil(this.ngUnsubscribe)
      );
  }

  public isBrowserPopupBlocked() {
    return true;
  }

  public loadAppInfos(): Observable<AppInfo[]> {
    ConsoleLogger.debug('EnvSvc.loadAppInfos(): begin');

    this.appInfos = [];
    return this.checkIfProxied()
      .pipe(
        switchMap((destinyAppInfo: AppInfo) => {
          if (destinyAppInfo) {
            this.destinyBuildNumber = destinyAppInfo.destinyBuildNumber;
            this.appInfos.push(destinyAppInfo);
            this.destinyChatBotEnabledAtDistrict = destinyAppInfo.destinyChatBotEnabled;

            ConsoleLogger.debug('EnvSvc.loadAppInfos(): proxy check complete');
            return this.loadSingletonAppInfos()
              .pipe(
                tap((singletonAppInfos: AppInfo[]) => {
                  ConsoleLogger.debug('EnvSvc.loadAppInfos(): singleton app infos loaded: ' + JSON.stringify(singletonAppInfos));
                  const destinyUrl = new URL(destinyAppInfo.url);
                  if (this.shouldBeProxied && this.shouldBeProxied == 'true') {
                    ConsoleLogger.debug('EnvSvc.loadAppInfos(): running in proxied mode, updating singleton app urls to use destiny host');
                    for (const info of singletonAppInfos) {
                      const app: Application = info.name as Application;
                      if (!this.getOverrideAppId(app)) {
                        const origUrl = new URL(info.url);
                        origUrl.protocol = destinyUrl.protocol;
                        origUrl.host = destinyUrl.host;
                        origUrl.port = destinyUrl.port;
                        info.url = origUrl.toString();
                      } else {
                        info.override = 'true';
                      }
                    }
                  }
                  this.appInfos = this.appInfos.concat(singletonAppInfos);
                })
              );
          } else {
            ConsoleLogger.error('EnvSvc.loadAppInfos(): Unable to find destiny appinfo');
            return throwError(() => 'Unable to find destiny appinfo');
          }
        }),
        catchError(err => {
          ConsoleLogger.error('EnvSvc.loadAppInfos(): Error loading app infos: ' + err);
          return throwError(() => err);
        }),
        tap(() => ConsoleLogger.debug('EnvSvc.loadAppInfos(): complete')),
        takeUntil(this.ngUnsubscribe)
      );
  }


  private loadSingletonAppInfos(): Observable<AppInfo[]> {
    ConsoleLogger.debug('EnvSvc.loadSingletonAppInfos(): begin');

    const singletonAppInfos$: Observable<AppInfo[]>[] = [];
    const nonOverriddenApps: Application[] = _.cloneDeep(EnvironmentService.singletonApps);

    for (const app of EnvironmentService.singletonApps) {
      let overrideAppId = this.getOverrideAppId(app);
      if (!overrideAppId) {
        if (app === Application.DASHBOARD) {
          overrideAppId = _.get(this.appConfig, ['local', 'DASHBOARD_BE_APPID_OVERRIDE']);
        } else if (app === Application.UNIFIED_SETTINGS) {
          overrideAppId = _.get(this.appConfig, ['local', 'UNIFIED_SETTINGS_BE_APPID_OVERRIDE']);
        } else if (app === Application.CLM) {
          overrideAppId = _.get(this.appConfig, ['local', 'CLM_APPID_OVERRIDE']);
        } else if (app === Application.PSARI) {
          overrideAppId = _.get(this.appConfig, ['local', 'PSARI_APPID_OVERRIDE']);
        } else if (app === Application.DESTINY_UI) {
          overrideAppId = _.get(this.appConfig, ['local', 'DESTINY_UI_APPID_OVERRIDE']);
        } else if (app === Application.JUNIPER_SIDECAR) {
          overrideAppId = _.get(this.appConfig, ['local', 'JUNIPER_SIDECAR_APPID_OVERRIDE']);
        }
      }


      if (overrideAppId) {
        ConsoleLogger.debug('EnvSvc.loadSingletonAppInfos(): override found for ' + app + ': ' + overrideAppId);
        _.remove(nonOverriddenApps, a => a === app);

        singletonAppInfos$.push(
          this.getAppInfo(overrideAppId, app, 'singletons')
            .pipe(
              map(a => [a]),
              catchError(() => {
                ConsoleLogger.error('EnvSvc.loadSingletonAppInfos(): Unable to find appInfo for override: ' + overrideAppId);
                return of([{name: app, appId: overrideAppId, url: 'http://follettsoftware.com/invalidAppId/' + overrideAppId}]);
              })
            )
        );
      }
    }

    if (nonOverriddenApps.length > 0) { // get AppInfos for any apps that were not overridden
      singletonAppInfos$.push(this.getAppInfos(nonOverriddenApps.join(',')));
    }

    return forkJoin(singletonAppInfos$)
      .pipe(
        map((results: AppInfo[][]) => {
          const singletonAppInfos: AppInfo[] = results.flatMap((ais: AppInfo[]) => ais);

          if (!this.isProductionBuild()) {
            const dbUrl = this.getUrlFromConfig(Application.DASHBOARD);
            if (dbUrl) {
              const ai: AppInfo = singletonAppInfos.find(a => a.name === Application.DASHBOARD);
              ai.url = dbUrl;
            }
            const settingsUrl = this.getUrlFromConfig(Application.UNIFIED_SETTINGS);
            if (settingsUrl) {
              const ai: AppInfo = singletonAppInfos.find(a => a.name === Application.UNIFIED_SETTINGS);
              ai.url = settingsUrl;
            }
            const juniperSidecarUrl = this.getUrlFromConfig(Application.JUNIPER_SIDECAR);
            if (juniperSidecarUrl) {
              const ai: AppInfo = singletonAppInfos.find(a => a.name === Application.JUNIPER_SIDECAR);
              if (ai) {
                ai.url = juniperSidecarUrl;
              }
            }
          }

          ConsoleLogger.debug('EnvSvc.loadSingletonAppInfos() complete: ' + JSON.stringify(singletonAppInfos));
          return singletonAppInfos;
        })
      );
  }


  public outputConfig() {
    if (!this.isProductionBuild()) {
      ConsoleLogger.debug('EnvSvc: config: ' + JSON.stringify(this.appConfig));
    }
  }

  private getOverrideAppId(app: Application) {
    const key = Util.getApplicationKey(app);
    return localStorage.getItem(key + '-override');
  }

  public resetUrl(app: Application){
    const appKey = Util.getApplicationKey(app);
    _.set(this.appConfig.clientUrls, [appKey, 'url'], this.appConfigOrig.clientUrls[appKey]?.url);
  }

  public getUrlFromConfig(app: Application) {
    const appKey = Util.getApplicationKey(app);
    return this.appConfig.clientUrls[appKey]?.url;
  }

  public getUrl(app: Application): string {
    let url = this.getUrlFromStorage(app);
    if (!url) {
      url = this.getUrlFromConfig(app);

      if (!url) {
        // Check if we have the appInfo for the Application from AASP
        const appInfo = this.appInfos.find( ( a:AppInfo ) => a.name === app);

        if (appInfo) {
          url = appInfo.url;
        }
      }
    }

    if (url) {
      if (app === Application.COLLECTIONS) {
        url += EnvironmentService.collectionsContext;
      } else if(app === Application.DD){
        url += EnvironmentService.ddContext;
      } else if (app === Application.DESTINY) {
        url += EnvironmentService.dboContext;
      } else if (app === Application.CLM) {
        url += EnvironmentService.clmContext;
      } else if (app === Application.DESTINY_AI) {
        url += EnvironmentService.destinyAiContext;
      } else if (app === Application.JUNIPER_SIDECAR) {
        url += EnvironmentService.juniperSidecarContext;
      }
    }

    return url;
  }

  public getUrlFromStorage(app: Application): string {
    let url;
    if (!this.isProductionBuild()) {
      const appId = this.getOverrideAppId(app);
      url = this.appInfos.find(ai => ai.appId === appId)?.url;
      if (!url && appId) {
        url = 'http://localhost/invalidAppId/' + appId;
      }
    }
    return url;
  }

  public isAppHidden(app: Application): boolean {
    if (!this.isProductionBuild()) {
      const appKey = Util.getApplicationKey(app);
      return localStorage.getItem(appKey + '-hidden') === 'true';
    }
    return false;
  }

  public isUsingFakeGrants(): boolean {
    return this.isProductionBuild() ? false : localStorage.getItem('useFake') === 'true';
  }

  public getEnvironment() {
    return this.appConfig.env;
  }

  public getPortalBackendUrl() {
      return this.appConfig.portal_backend_url + this.appConfig.portal_backend_context;
  }

  public isThemePickerEnabled(): boolean {
    return this.isProductionBuild() ? false : this.appConfig.themePickerEnabled;
  }

  public isMessageSenderEnabled(): boolean {
    return this.isProductionBuild()  ? false : this.appConfig.messageSenderEnabled;
  }

  public isSecretsEnabled(): boolean {
    return !this.isProductionBuild();
  }

  public getHeartbeatFrequency() {
    return _.get(this.appConfig, 'heartbeatFrequency', 30000);
  }

  public getAccessTokenExpCheckFrequency() {
    return _.get(this.appConfig, 'accessTokenExpCheckFrequency', 30000);
  }

  public getLogoutTimeout() {
    return _.get(this.appConfig, 'logoutTimeout', 30000);
  }

  public isProductionBuild(): boolean {
    return environment.production;
  }
  public isLocal(): boolean {
    return this.appConfig?.env === 'local';
  }
  public isDly(): boolean {
    return this.appConfig?.env === 'dly';
  }
  public isQat(): boolean {
    return this.appConfig?.env === 'qat';
  }
  public isPrd(): boolean {
    return this.appConfig.env === 'prd';
  }

  public getAppInfos(apps: string): Observable<AppInfo[]> {
    const url = this.getPortalBackendUrl() + '/authenticate/appInfos';
    const params = {
      apps: apps
    };
    return this.http.get<AppInfo[]>(url, { params, withCredentials: true });
  }

  public getAppInfo(appId: string, portalAppName: string, addl?: string): Observable<AppInfo> {
    const url = this.getPortalBackendUrl() + '/authenticate/appInfo';
    const params: any = {
      appId: appId,
      portalAppName: portalAppName
    };
    if (addl) {
      params['addl'] = addl;
    }
    return this.http.get<AppInfo>(url,  { params, withCredentials: true });
  }

  public isUnsLocalAndOverriden(app: Application) {
    return app === Application.UNIFIED_SETTINGS && this.isLocal() && !!this.appConfigOrig.clientUrls['UNIFIED_SETTINGS']?.url;
  }

  /**
   * Try to call the backend w/ the frontend host. If this call succeeds, we are proxied. If it fails, we should not
   * be proxied, and should be able to call our backend directly at the 'real' backend url.
   */
  private getAppInfoProxied(appId: string, portalAppName: string): Observable<AppInfo> {
    const currentUrl: URL = new URL(this.location.href);
    const url = currentUrl.protocol + '//' + currentUrl.host + '/portalapi/authenticate/appInfo';
    const params = {
      appId: appId,
      portalAppName: portalAppName,
      addl: 'proxied'
    };

    return this.http.get<AppInfo>(url,  { params, withCredentials: true })
      .pipe(
        catchError(err => {
          // If the call to the "proxied" backend fails, try to hit the "real" backend url
          ConsoleLogger.debug('EnvSvc.getAppInfoProxied(): proxied check failed, trying non-proxied', err);
          return this.getAppInfo(appId, portalAppName, 'proxied-error');
        })
      );
  }



  public checkIfProxied(): Observable<AppInfo> {
    ConsoleLogger.debug('EnvSvc.checkIfProxied(): ' + this.location.href);

    const currentUrl: URL = new URL(this.location.href);

    let destinyAppId = currentUrl.searchParams.get('appId');
    if (!destinyAppId) {
      destinyAppId = localStorage.getItem('district_appId');
    } else {
      localStorage.setItem('district_appId', destinyAppId);
    }

    if(destinyAppId) {
      return this.getAppInfoProxied(destinyAppId, Application.DESTINY)
        .pipe(
          switchMap((destinyInfo: AppInfo) => {
            const portalUrlFromOrigConfig = this.appConfigOrig.clientUrls['PORTAL']?.url;

            const popupsEnabled: string = destinyInfo.popupsEnabled;
            if(popupsEnabled !== undefined && popupsEnabled === 'false') {
              this.popupEnabled = 'false';
            }
            let shouldBeProxied: string = destinyInfo.proxy;
            const destinyUrl: URL = new URL(destinyInfo.url);
            const portalUrl: URL = new URL(portalUrlFromOrigConfig);
            const launchedFromProxy = portalUrl.host !== currentUrl.host;
            localStorage.setItem('proxy-check-complete', 'false');

            if (shouldBeProxied === undefined || shouldBeProxied === 'auto') {
              // check for 3rd party cookies
              // If we can set a cookie, assume we're good as a proxy and set shouldBeProxied to true
              // If we can't set a cookie, assume we're not good as a proxy and set shouldBeProxied to false
              shouldBeProxied = 'true'; // for now...
            }

            if(this.appConfig.env === 'local' || this.appConfig.env === 'dev'){
              shouldBeProxied = _.get(this.appConfig, ['local', 'forceProxy']) ? 'true' : 'false';
            }

            if( launchedFromProxy && shouldBeProxied === 'false' ) {
              const url = this.appConfigOrig.clientUrls['PORTAL']?.url + currentUrl.pathname + currentUrl.search;
              ConsoleLogger.debug('EnvSvc.checkIfProxied(): Redirect to non-proxied portal. window.location.href => ' + url);
              this.addDebugMessage('', new DebugMessage(undefined, 'Portal', 'Portal', 'logevent', {fn: 'reload-notproxied', addl: 'reload-notproxied'}, undefined));
              this.http.get(this.appConfigOrig.clientUrls['PORTAL']?.url + '/portalapi/internal/log', { params: {addl: 'reload-notproxied'}, withCredentials: true }).pipe(take(1), catchError(() => of(undefined))).subscribe();
              this.reloadEntireApp(url);
              return NEVER as Observable<AppInfo>;
            } else if ( !launchedFromProxy && shouldBeProxied === 'true') {
              const url = destinyUrl.protocol + '//' + destinyUrl.host + currentUrl.pathname + currentUrl.search;
              ConsoleLogger.debug('EnvSvc.checkIfProxied(): Redirect to proxied portal. window.location.href => ' + url);
              this.addDebugMessage('', new DebugMessage(undefined, 'Portal', 'Portal', 'logevent', {fn: 'reload-proxied', addl: 'reload-proxied'}, undefined));
              this.http.get(destinyUrl.protocol + '//' + destinyUrl.host + '/portalapi/internal/log', { params: {addl: 'reload-proxied'}, withCredentials: true }).pipe(take(1), catchError(() => of(undefined))).subscribe();
              this.reloadEntireApp(url);
              return NEVER as Observable<AppInfo>;
            } else {
              // (launchedFromProxy && shouldBeProxied) || (!launchedFromProxy && !shouldBeProxied)
              ConsoleLogger.debug('EnvSvc.checkIfProxied(): No redirect needed');
              this.portal_frontend_proxy_url = launchedFromProxy ? destinyUrl.protocol + '//' + destinyUrl.host + '/portal' : undefined;
              this.appConfig.portal_backend_url = launchedFromProxy ? destinyUrl.protocol + '//' + destinyUrl.host : this.appConfig.portal_backend_url;
              this.shouldBeProxied = shouldBeProxied;
              localStorage.setItem('proxy-check-complete', 'true');
              this._appInitComplete.next(true);
              return of(destinyInfo);
            }
          })
        );
    } else {
      localStorage.removeItem('district_appId');
      ConsoleLogger.error('EnvSvc.checkIfProxied(): No destiny appId found');
      return of(undefined);
    }
  }

  public isFUN_ENABLED(): boolean {
    // eslint-disable-next-line no-constant-binary-expression
    let isEnabled =  (true && this.getRecommendedUNVersion() === UN_CURRENT_VERSION);
    if (!this.isPrd()) {
      isEnabled = isEnabled && localStorage.getItem('fss_unv2_disabled') !== 'true';
    }

    return isEnabled;
  }

  public isCUSTOM_HOMEPAGE(): boolean {
    let isEnabled = this.isFUN_ENABLED();
    if (!this.isPrd()) {
      isEnabled = isEnabled && localStorage.getItem('fss_custom_homepage_disabled') !== 'true';
    }
    return isEnabled;
  }

  public getRecommendedUNVersion(): number {
    let recommendedVersion = UN_CURRENT_VERSION;
    const destinyBuildNumber = this.destinyBuildNumber;
    if (destinyBuildNumber) {
      const majorVersion = destinyBuildNumber.split('.')[0];
      if(majorVersion && Number(majorVersion) < 23) {
        recommendedVersion = 1;
      }
    }
    return recommendedVersion;
  }

  public isDestinyUIEnabled(): boolean {
    return true;
  }


  public getUnsBackendUrl(): string {
    return (this.isLocal() ? 'http://' : '') + this.appInfos.find(ai => ai.name === Application.UNIFIED_SETTINGS)?.backendUrl;
  }


  public reloadEntireApp(url: string) {
    this.processDebugMessageQueue()
      .pipe(
        catchError(() => of(undefined)),
        take(1)
      )
      .subscribe(() => {
        ConsoleLogger.debug('EnvSvc: Reloading entire app: ' + url);
        this.location.href = url;
      });
  }

  public getKioskMode(): boolean {
    return localStorage.getItem('kioskMode') === 'true';
  }

  public setKioskMode(enabled: boolean): void {
    localStorage.setItem('kioskMode', String(enabled));
  }

  /** -------------------------------------------------------------------- */
  public checkDebuggingEnabled(ulm: UserLoginModel): Observable<boolean> {
    if (this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER) { // Once enabled, always enabled for the session because of the bxId.
      return of(true);
    } else {

      let userGuid = '';
      if (ulm) {
        if (ulm.getUserGuid()) {
          userGuid = ulm.getUserGuid();
        }
      }

      if (userGuid.toLowerCase() === UserLoginModel.GUEST) {
        const siteGuid = ulm.getSiteGuid();
        if (siteGuid === 'D7438BDB-FAF2-404B-A032-B4A622BFC098') { // Loudoun, Briar Woods HS
          this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER = true;
          this._startDebugMessageTimer();
          return of(true);
        } else {
          userGuid = ''; // Force to empty userGuid to avoid logging guest debug messages, unless we have a matching bxId
        }
      }

      const url = this.getPortalBackendUrl() + '/user/unsettings/portaldebug/loggingEnabled?userGuid=' + userGuid + '&bxId=' + this.bxid;
      return this.http.get<boolean>(url, {withCredentials: true})
        .pipe(
          catchError((error) => {
            ConsoleLogger.error('EnvSvc: Error getting debug info', error);
            return of(false);
          }),
          tap((res: boolean) => {
            ConsoleLogger.debug('EnvSvc: Debugging enabled for user: ' + res);
            const wasEnabled = this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER;
            this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER = (res === true);

            if (!wasEnabled && this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER) {
              this._startDebugMessageTimer();
            } else if (wasEnabled && !this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER) {
              this._stopDebugMessageTimer();
            }
          })
        );
    }
  }

  private _stopDebugMessageTimer() {
    if (this.debugTimerUnsubscribe) {
      this.debugTimerUnsubscribe.next(undefined);
      this.debugTimerUnsubscribe.complete();
      this.debugTimerUnsubscribe = null;
    }
  }


  private _startDebugMessageTimer() {
    if (this.debugTimerUnsubscribe) {
      return; // Already running
    }

    this.debugTimerUnsubscribe = new Subject<void>();

    interval(30000)
      .pipe(
        catchError((error) => {
          ConsoleLogger.debug('EnvSvc._startDebugMessageTimer(): Error in debug message timer', error);
          return of();
        }),
        switchMap(() => this.processDebugMessageQueue()),
        takeUntil(this.debugTimerUnsubscribe)
      )
      .subscribe();
  }



  public processDebugMessageQueue(): Observable<any> {
    if (this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER === false) {
      this._debugMessageQueue = [];
      return of(undefined);
    }

    const messagesToProcess: PortalDebugMessage[] = [...this._debugMessageQueue]; // Take a snapshot
    this._debugMessageQueue = []; // Clear the queue

    // Always add a heartbeat message
    messagesToProcess.unshift(new PortalDebugMessage('', this.bxid, [{id: undefined, source: 'Portal', destination: 'Portal', type: 'logevent', message: 'heartbeat', timestamp: new Date()}]));

    // Group by userGuid+bxId
    const grouped = new Map<string, PortalDebugMessage>();
    messagesToProcess.forEach(pdm => {
      const key = `${pdm.userGuid}_${pdm.bxId}`;
      if (!grouped.has(key)) {
        grouped.set(key, new PortalDebugMessage(pdm.userGuid, pdm.bxId, []));
      }
      grouped.get(key)!.messages.push(...pdm.messages);
    });

    const updates$ = [];
    for (const pdm of grouped.values()) {
      updates$.push(this.updateLog(pdm)
        .pipe(
          catchError((error) => {
            ConsoleLogger.error('EnvSvc.processDebugMessageQueue(): Error updating debug log', error);
            this._debugMessageQueue.push(pdm);
            return of(false);
          }),
          tap((success) => {
            if (!success) {
              this._debugMessageQueue.push(pdm);
            }
          }),
          take(1),
          takeUntil(this.ngUnsubscribe)
        ));
    }
    return forkJoin(updates$);

  }


  /** -------------------------------------------------------------------- */
  public addDebugMessage(userGuid: string, message: DebugMessage) {
    if (this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER) {
      this._debugMessageQueue.push(new PortalDebugMessage(userGuid, this.bxid, [message]));
    }
  }


  /** -------------------------------------------------------------------- */
  private updateLog(portalDebugMessage: PortalDebugMessage): Observable<boolean> {
    if (this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER) {
      const url = this.getUnsBackendUrl() + '/portallog/updatelog';
      return this.http.put<{statusCode: number}>(url, portalDebugMessage, {withCredentials: true})
        .pipe(
          catchError((error) => {
            ConsoleLogger.debug('EnvSvc.updateLog(): Failed to update', error);
            return of({statusCode: 500});
          }),
          map((res: {statusCode: number}) => {
            if (res.statusCode === 200) {
              return true;
            } else if (res.statusCode === 404) {
              ConsoleLogger.debug('EnvSvc.updateLog(): Disabling logging because user settings not found');
              this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER = false;
              return true;
            }
            return false;
          })
        );
    }
    return of(true);
  }

  public isMessageDebuggingEnabled(): boolean {
    return this.IS_MESSAGE_DEBUGGING_ENABLED_FOR_USER;
  }

  public getGoFollettLink(): string {
    if (this.appConfig &&  this.appConfig.gofollett_url) {
      return this.appConfig.gofollett_url;
    } else {
      ConsoleLogger.debug('EnvSvc.getGoFollettLink(): appConfig or gofollett_url missing');
      return '';
    }
  }
}
